import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Encounter, Questionnaire, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Button, Drawer, Group, Paper, Stack } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { v4 as uuidv4 } from "uuid";

import { Loading } from "@/components/Loading";
import { PatientContext } from "@/contexts/patient";
import { CPTCodeDrawerAction, CPTCodeDrawerActions } from "@/encounters/constants/cpt-code";
import { useRegisterEncounterChildSave } from "@/encounters/contexts/EncounterChildSaveContext";
import { loadCptQuestionnaireWithCombinedValueSet } from "@/encounters/services/cpt-questionnaire";
import { Status } from "@/encounters/zod-schemas/encounters";
import { usePracticeId } from "@/hooks/usePracticeId";
import QuestionnaireForm from "@/intake-forms/components/QuestionnaireForm";
import { useAppDispatch } from "@/store";
import {
  deleteProcedure,
  selectAllActiveProcedures,
  selectTargetProcedure,
  setProcedure,
  setTargetProcedure,
  updateProcedure,
} from "@/store/slices/procedures";
import { toDrawerRootProps } from "@/utils/mantine-drawer-stack";
import ProceduresForm, {
  ProceduresFormType,
  ProceduresFormTypeWithId,
} from "@/zod-schemas/procedures";

import { handleQuestionnaireResponseCreation } from "../services/questionnaire-response";
import { parseProcedureQuestionnaireResponse } from "../utils/procedure";

import { ProcedureCodingFilterProvider } from "./ProcedureCodingFilterProvider";

type EncounterProceduresDrawerProps<T> = {
  register: (id: T) => {
    opened: boolean;
    onClose: () => void;
    stackId: T;
  };
  closeAll: () => void;
  drawerName: T;
  submitTrigger: number;
  encounter: Encounter;
  form: UseFormReturnType<ProceduresFormType>;
  targetCptCode?: ProceduresFormTypeWithId;
  lastAction?: CPTCodeDrawerAction;
  mainQuestionnaireUrl: string;
  markProceduresListSaved: () => void;
  markDrawerFormSaved: () => void;
};

const EncounterProceduresDrawer = <T,>({
  register,
  closeAll,
  drawerName,
  submitTrigger,
  form,
  targetCptCode,
  encounter,
  lastAction,
  mainQuestionnaireUrl,
  markProceduresListSaved,
  markDrawerFormSaved,
}: EncounterProceduresDrawerProps<T>) => {
  const medplum = useMedplum();
  const { patient } = useContext(PatientContext);
  const practiceId = usePracticeId(encounter);
  const [previousSubmitTrigger] = useState(submitTrigger);
  const [isLoading, setIsLoading] = useState(false);
  const [cptCodeQuestionnaire, setCptCodeQuestionnaire] = useState<Questionnaire | undefined>(
    undefined,
  );

  const dispatch = useAppDispatch();
  const procedures = useSelector(selectAllActiveProcedures);
  const targetProcedure = useSelector(selectTargetProcedure);

  const isEncounterActive =
    encounter.status !== Status.Finished && encounter.status !== Status.Cancelled;

  const checkForDuplicatedProcedures = (procedures: ProceduresFormTypeWithId[]) => {
    const seen = new Set();

    for (const procedure of procedures) {
      if (seen.has(procedure.cptCode?.code)) return true;
      seen.add(procedure.cptCode?.code);
    }

    return false;
  };

  const handleCptCodeData = (data: ProceduresFormType) => {
    const parsedValues = ProceduresForm.parse(data);

    const hasDuplicatedProcedures =
      !!procedures.find((procedure) => procedure.cptCode?.code === parsedValues.cptCode?.code) &&
      targetProcedure?.cptCode?.code !== parsedValues.cptCode?.code;

    if (hasDuplicatedProcedures) {
      form.setErrors({ cptCode: "This procedure code has already been added." });
      return;
    }

    const procedure = procedures.find((procedure) => procedure.id === targetCptCode?.id);

    if (!procedure) {
      const newProcedure = { ...parsedValues, id: uuidv4(), exists: false };
      dispatch(setProcedure(newProcedure));
    } else {
      const updatedProcedure = { ...parsedValues, id: procedure.id, exists: procedure.exists };
      dispatch(updateProcedure({ id: procedure.id, changes: updatedProcedure }));
    }

    dispatch(setTargetProcedure(undefined));
    form.reset();
    markDrawerFormSaved();
    return true;
  };

  const handleSubmit = async (statusOverride?: QuestionnaireResponse["status"]) => {
    const getStatus = () => {
      if (statusOverride) return statusOverride;
      if (!isEncounterActive) return "amended";

      return lastAction === CPTCodeDrawerActions.Save ? "in-progress" : "completed";
    };

    const hasDuplicatedProcedures = checkForDuplicatedProcedures(procedures);

    if (hasDuplicatedProcedures) return;

    const { savedProcedures, failures } = await handleQuestionnaireResponseCreation({
      medplum,
      cptCodeData: procedures,
      status: getStatus(),
      patientId: patient?.id,
      encounterId: encounter.id,
      mainQuestionnaireUrl,
    });

    if (!savedProcedures.length && !failures.length) return;

    // Every procedure that saved is recorded, even when another failed, so a retry updates it
    // instead of creating a duplicate.
    const savedProcedureIds = new Set(savedProcedures.map(({ procedureId }) => procedureId));
    procedures.forEach((procedure) => {
      if (procedure.exists || !savedProcedureIds.has(procedure.id)) return;
      dispatch(deleteProcedure(procedure.id));
    });

    const parsedQuestionnaireResponses = parseProcedureQuestionnaireResponse(
      savedProcedures.map(({ questionnaireResponse }) => questionnaireResponse),
    );
    parsedQuestionnaireResponses.forEach((questionnaireResponse) => {
      dispatch(setProcedure(questionnaireResponse));
    });

    if (failures.length) throw failures[0];

    markProceduresListSaved();
    markDrawerFormSaved();
    if (!register(drawerName).opened) form.reset();
  };

  useRegisterEncounterChildSave(({ status }) => handleSubmit(status));

  const hasCptCode = !!form.getValues()?.cptCode?.code;

  useEffect(() => {
    if (!hasCptCode) form.reset();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCptCode]);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      const questionnaire = await loadCptQuestionnaireWithCombinedValueSet(medplum, practiceId);
      setCptCodeQuestionnaire(questionnaire);
      setIsLoading(false);
    };
    fetchInitialData();
  }, [medplum, practiceId]);

  useEffect(() => {
    if (submitTrigger && submitTrigger !== previousSubmitTrigger) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitTrigger]);

  return (
    <Drawer.Root
      {...toDrawerRootProps(register(drawerName))}
      size={512}
      offset={8}
      position="right"
      onClose={() => {
        dispatch(setTargetProcedure(undefined));
        form.reset();
        markDrawerFormSaved();
        closeAll();
      }}
    >
      <Drawer.Overlay />
      <Drawer.Content>
        <form
          data-testid="encounter-procedure-drawer"
          onSubmit={form.onSubmit((values) => {
            const isSuccess = handleCptCodeData(values);
            if (isSuccess) closeAll();
          })}
        >
          <Drawer.Header mb="lg" px="md" bg="gray.0">
            <Group w="100%" justify="space-between">
              <Group gap="xs">
                <Drawer.CloseButton size="xl" />
                <Drawer.Title c="dark.9" variant="bold">
                  Procedures
                </Drawer.Title>
              </Group>
              <Button
                type="submit"
                disabled={!hasCptCode || isLoading}
                data-testid="encounter-procedure-save-button"
              >
                Save
              </Button>
            </Group>
          </Drawer.Header>
          <Paper px="lg" pb="xl" radius="md">
            {isLoading ? (
              <Loading height={96} />
            ) : (
              <Stack mt="sm" gap="xl">
                <ProcedureCodingFilterProvider encounter={encounter}>
                  <QuestionnaireForm form={form} group={cptCodeQuestionnaire?.item?.[0]} />
                </ProcedureCodingFilterProvider>
              </Stack>
            )}
          </Paper>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
};

export default EncounterProceduresDrawer;
